import { ReactNode } from 'react';
import { Zap, Gauge, Battery } from 'lucide-react';
import { DashboardCard } from '@/components/ui/dashboard-card.tsx';
import style from './overview-cards.module.scss';
import { useGetPlantsQuery } from '@/features/plants/hooks/queries/use-get-plants.tsx';

const MetricCard = ({
  label,
  value,
  unit,
  icon,
  isLoading,
}: {
  label: string;
  value: string;
  unit?: string;
  icon: ReactNode;
  isLoading?: boolean;
}) => (
  <DashboardCard className="h-[90px] min-w-[220px]" isLoading={isLoading}>
    <div className="flex justify-between">
      <div className="flex flex-col gap-3 px-2">
        <span className="text-muted-foreground text-base text-nowrap">{label}</span>
        <div className="flex items-end gap-2">
          <span className="text-2xl font-semibold">
            {value}
            {unit ? <span className="text-muted-foreground text-xl"> {unit}</span> : null}
          </span>
        </div>
      </div>
      <div className="self-center">
        <div className="flex size-14 items-center justify-center">{icon}</div>
      </div>
    </div>
  </DashboardCard>
);

export const OverviewCards = () => {
  const { data: plants, isLoading } = useGetPlantsQuery({ includeOverview: true });

  const plantSummary = {
    total: plants?.length,
    operational: plants?.filter((p) => p?.mqttStatus && p?.mqttStatus > 0).length,
    totalCapacity: plants?.reduce((sum, p) => {
      if (!p?.technicalSpecifications.storageCapacity) {
        return sum;
      }

      return sum + +p?.technicalSpecifications.storageCapacity;
    }, 0),
    avgSoC:
      (plants &&
        plants?.reduce((sum, p) => {
          if (!p?.batteryStateOfCharge) {
            return sum;
          }

          return sum + +p?.batteryStateOfCharge;
        }, 0) / plants?.length) ||
      0,
    availableEnergy:
      plants?.reduce((sum, p) => {
        if (!p?.technicalSpecifications.storageCapacity || !p?.batteryStateOfCharge) {
          return sum;
        }
        return (
          sum + (+p?.technicalSpecifications.storageCapacity * +p.batteryStateOfCharge) / 100
        );
      }, 0) || 0,
    storageCapacity: plants?.reduce((sum, p) => {
      if (!p?.technicalSpecifications.storageCapacity) {
        return sum;
      }
      return sum + +p.technicalSpecifications.storageCapacity;
    }, 0),
  };
  return (
    <div className={style.overview_cards}>
      <div className={style.overview_cards__grid}>
        <MetricCard
          label="Active Plants"
          value={`${plantSummary.operational}/${plantSummary.total}`}
          icon={<Zap className="text-gray-500" size={32} />}
          isLoading={isLoading}
        />
        <MetricCard
          label="Storage Capacity"
          value={`${plantSummary.storageCapacity}`}
          unit="kWh"
          icon={<Battery className="text-gray-500" size={32} />}
          isLoading={isLoading}
        />
        <MetricCard
          label="Available Energy"
          value={`${plantSummary.availableEnergy.toFixed(0)}`}
          unit="kWh"
          icon={<Zap className="text-gray-500" size={32} />}
          isLoading={isLoading}
        />
        <MetricCard
          label="Average SoC"
          value={`${plantSummary.avgSoC.toFixed(1)}`}
          unit="%"
          icon={<Gauge className="text-gray-500" size={32} />}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
};

export default OverviewCards;
